import '@/App.css';
import { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Link, useSearchParams, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider, useQuery, useQueryClient } from '@tanstack/react-query';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import { TimezoneProvider } from '@/components/TimezoneContext';
import WorkspaceShell from '@/workspace/WorkspaceShell';
import AnalyticsPage from '@/analytics/AnalyticsPage';
import OverviewPage from '@/trace/OverviewPage';
import CampaignsPage from '@/trace/CampaignsPage';
import AttributionPage from '@/trace/AttributionPage';
import CustomersPage from '@/trace/CustomersPage';
import ReportsPage from '@/trace/ReportsPage';
import ConnectionsPage from '@/components/ConnectionsPage';
import SendbackPage from '@/components/SendbackPage';
import LogsPage from '@/components/LogsPage';
import AutomationsPage from '@/components/AutomationsPage';
import AutomationBuilderPage from '@/components/AutomationBuilderPage';
import SettingsPage from '@/components/SettingsPage';
import OnboardingFlow from '@/components/OnboardingFlow';
import AdminTenantPicker from '@/components/AdminTenantPicker';
import AcceptInvitePage from '@/components/AcceptInvitePage';
import LoginPage from '@/components/LoginPage';
import { ContactDetailModal } from '@/components/ContactDetailModal';
import { useMe } from '@/hooks/useMe';
import { deleteContactRecords } from '@/lib/recordActions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function AppShell() {
  const qc = useQueryClient();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [authToken, setAuthToken] = useState(() => localStorage.getItem('tether_auth'));
  const [adminToken, setAdminToken] = useState(() => sessionStorage.getItem('tether_admin_token'));
  const me = useMe();
  const contactOpener = useRef(null);   // what opened the contact inspector, so closing it returns focus there

  const handleLogin = (data) => {
    // Operator credentials → workspace picker, no tenant session yet
    if (data.admin) {
      sessionStorage.setItem('tether_admin_token', data.token);
      setAdminToken(data.token);
      return;
    }
    // Fresh tenant session: drop EVERYTHING cached — an admin hopping between
    // workspaces must never see the previous tenant's data flash through.
    qc.clear();
    localStorage.setItem('tether_auth', data.token);
    if (data.tenant) {
      localStorage.setItem('tether_me', JSON.stringify({
        email: data.email, name: data.tenant.name, site_key: data.tenant.site_key,
        tracking_domain: data.tenant.tracking_domain || null, tenant_id: data.tenant.id,
        funnel_type: data.tenant.funnel_type || null,
        domain_verified: !!data.tenant.domain_verified,
        onboarded: data.tenant.onboarded !== false,
      }));
    }
    setAuthToken(data.token);
  };
  const handleLogout = () => {
    localStorage.removeItem('tether_auth');
    localStorage.removeItem('tether_me');
    sessionStorage.removeItem('tether_admin_token');
    qc.clear();
    setAdminToken(null);
    setAuthToken(null);
  };

  // The fetch interceptor broadcasts this when the backend answers 401
  // (expired token, deleted tenant) — drop back to the login screen.
  useEffect(() => {
    const onForcedLogout = () => setAuthToken(null);
    window.addEventListener('tether:logout', onForcedLogout);
    return () => window.removeEventListener('tether:logout', onForcedLogout);
  }, []);


  const selectedContactId = searchParams.get('contact');
  const legacyContactTab = ['overview', 'attribution', 'urls', 'sales'].includes(searchParams.get('tab')) ? searchParams.get('tab') : 'overview';
  const selectedTab       = searchParams.get('contactTab') || legacyContactTab;
  const modalOpen         = !!selectedContactId;

  // ── Contacts list ──────────────────────────────────────────
  const {
    data:      contacts  = [],
    isLoading: initialLoad,
    isFetching: loading,
    isError: contactsError,
  } = useQuery({
    queryKey: ['contacts'],
    queryFn:  () => fetch(`${API}/contacts`).then(r => {
      if (!r.ok) throw new Error('Failed to load contacts');
      return r.json();
    }),
    refetchInterval: 15_000,
    enabled: !!authToken,
  });

  // ── Stats ──────────────────────────────────────────────────
  const { data: stats = { total_contacts: 0, total_visits: 0, today_visits: 0 } } = useQuery({
    queryKey: ['stats'],
    queryFn:  () => fetch(`${API}/stats`).then(r => {
      if (!r.ok) throw new Error('Failed to load stats');
      return r.json();
    }),
    refetchInterval: 15_000,
    enabled: !!authToken,
  });

  // ── Actions ────────────────────────────────────────────────
  // Public route: teammates land here from invite emails — no auth required
  if (location.pathname === '/accept-invite') {
    return <AcceptInvitePage onLogin={handleLogin} />;
  }

  // Operator session without a tenant yet → workspace picker
  if (!authToken && adminToken) {
    return (
      <AdminTenantPicker
        adminToken={adminToken}
        onPick={handleLogin}
        onExit={() => { sessionStorage.removeItem('tether_admin_token'); setAdminToken(null); }}
      />
    );
  }

  // Show login page if not authenticated — all hooks already called above
  if (!authToken) return <LoginPage onLogin={handleLogin} />;

  // First login on a fresh workspace → guided setup before the dashboard
  if (me && me.onboarded === false) return <OnboardingFlow />;

  const handleRefresh = () => {
    qc.invalidateQueries({ queryKey: ['contacts'] });
    qc.invalidateQueries({ queryKey: ['stats'] });
  };

  const handleSelectContact = (id, tab = 'overview') => {
    contactOpener.current = document.activeElement;
    setSearchParams(current => {
    const next = new URLSearchParams(current);
    next.set('contact', id);
    next.set('contactTab', tab);
    return next;
    });
  };
  const handleCloseModal = () => setSearchParams(current => {
    const next = new URLSearchParams(current);
    next.delete('contact');
    next.delete('contactTab');
    return next;
  }, { replace: true });

  const handleDeleteContact = async contactId => {
    const { deletedIds } = await deleteContactRecords(API, [contactId]);
    if (!deletedIds.length) {
      toast.error('Could not delete this contact. Please try again.');
      return false;
    }
    qc.invalidateQueries({ queryKey: ['contacts'] });
    qc.invalidateQueries({ queryKey: ['stats'] });
    toast.success('Contact deleted');
    return true;
  };

  const handleBulkDelete = async ids => {
    if (!ids?.length) return { deletedIds: [], failedIds: [] };
    const outcome = await deleteContactRecords(API, ids);
    const deleted = outcome.deletedIds.length;
    const failed = outcome.failedIds.length;
    if (deleted) {
      qc.invalidateQueries({ queryKey: ['contacts'] });
      qc.invalidateQueries({ queryKey: ['stats'] });
      toast.success(`${deleted} contact${deleted !== 1 ? 's' : ''} deleted`);
    }
    if (failed) toast.error(`${failed} contact${failed !== 1 ? 's' : ''} could not be deleted. They remain selected so you can retry.`);
    return outcome;
  };

  const shared = {
    contacts, loading, initialLoad, stats, contactsError,
    onRefresh:       handleRefresh,
    onSelectContact: handleSelectContact,
    onDeleteContact: handleDeleteContact,
    onBulkDelete:    handleBulkDelete,
  };

  return (
    <div className="app-canvas">
      <WorkspaceShell
        me={me}
        onLogout={handleLogout}
        adminMode={!!adminToken}
        onSwitchWorkspace={adminToken ? () => {
          // Keep the admin session; drop only the tenant session → picker
          localStorage.removeItem('tether_auth');
          localStorage.removeItem('tether_me');
          qc.clear();
          setAuthToken(null);
        } : undefined}
      >
        <Routes>
          <Route path="/"            element={<OverviewPage />} />
          <Route path="/campaigns"   element={<CampaignsPage onSelectContact={handleSelectContact} />} />
          <Route path="/attribution" element={<AttributionPage />} />
          <Route path="/journeys"    element={<CustomersPage tab="journeys" onSelectContact={handleSelectContact} shared={shared} me={me} />} />
          <Route path="/people"      element={<CustomersPage tab="people" onSelectContact={handleSelectContact} shared={shared} me={me} />} />
          <Route path="/leads"       element={<CustomersPage tab="people" onSelectContact={handleSelectContact} shared={shared} me={me} />} />
          <Route path="/visitors"    element={<CustomersPage tab="visitors" onSelectContact={handleSelectContact} shared={shared} me={me} />} />
          <Route path="/stealth"     element={<CustomersPage tab="stealth" onSelectContact={handleSelectContact} shared={shared} me={me} />} />
          <Route path="/sales"       element={<CustomersPage tab="sales" onSelectContact={handleSelectContact} shared={shared} me={me} />} />
          <Route path="/reports"     element={<ReportsPage />} />
          <Route path="/connections" element={<ConnectionsPage />} />
          <Route path="/sendback"    element={<SendbackPage />} />
          <Route path="/automations" element={<AutomationsPage />} />
          <Route path="/automations/new" element={<AutomationBuilderPage />} />
          <Route path="/automations/builder/:id" element={<AutomationBuilderPage />} />
          <Route path="/traffic"     element={<AnalyticsPage mode="traffic" onSelectContact={handleSelectContact} />} />
          <Route path="/analytics"   element={<AnalyticsPage mode="traffic" onSelectContact={handleSelectContact} />} />
          <Route path="/logs"        element={<LogsPage onSelectContact={handleSelectContact} />} />
          <Route path="/settings"    element={<SettingsPage />} />
          <Route path="*" element={<section className="panel empty-state">
            <h1>Page not found</h1>
            <p>This link may have changed. Your workspace is still here.</p>
            <Link to="/" className="btn primary">Back to overview</Link>
          </section>} />
        </Routes>
      </WorkspaceShell>

      <ContactDetailModal
        contactId={selectedContactId}
        defaultTab={selectedTab}
        open={modalOpen}
        onClose={handleCloseModal}
        returnFocusTo={contactOpener}
        onDelete={handleDeleteContact}
      />
    </div>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TimezoneProvider>
        <BrowserRouter>
          <AppShell />
          <Toaster position="bottom-center" closeButton toastOptions={{
            style: { backgroundColor: '#20211f', border: '1px solid #444', borderRadius: 0,
              color: '#ffffff', fontFamily: 'var(--font)', boxShadow: '0 8px 40px rgba(23,24,19,0.07)' },
          }} />
        </BrowserRouter>
      </TimezoneProvider>
    </QueryClientProvider>
  );
}
